import { WRITE_IN_MAX_LENGTH, type TextResponse } from '@assessments/shared'
import { cn } from '@/lib/utils'

interface Props {
  value: TextResponse
  onChange: (response: TextResponse) => void
  labelledBy: string
  describedBy?: string
  disabled?: boolean
}

/**
 * Single-line free text, capped at WRITE_IN_MAX_LENGTH characters (FR-008). A live
 * counter and an at-limit message make the cap visible, so hitting it reads as
 * intentional rather than input silently stopping.
 */
export function WriteInQuestion({ value, onChange, labelledBy, describedBy, disabled }: Props) {
  const count = value.text.length
  const atLimit = count >= WRITE_IN_MAX_LENGTH
  const counterId = `${labelledBy}-count`

  return (
    <div className="mt-3">
      <input
        type="text"
        aria-labelledby={labelledBy}
        aria-describedby={[describedBy, counterId].filter(Boolean).join(' ') || undefined}
        maxLength={WRITE_IN_MAX_LENGTH}
        disabled={disabled}
        value={value.text}
        onChange={(e) => onChange({ kind: 'text', text: e.target.value })}
        className="w-full rounded-button border border-input px-3 py-2 text-sm text-body focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-50"
      />
      <div
        id={counterId}
        aria-live="polite"
        className={cn(
          'mt-1 flex justify-between gap-3 text-xs',
          atLimit ? 'text-danger' : 'text-subtle',
        )}
      >
        <span>{atLimit ? `Character limit reached (max ${WRITE_IN_MAX_LENGTH})` : ''}</span>
        <span>
          {count}/{WRITE_IN_MAX_LENGTH}
        </span>
      </div>
    </div>
  )
}
